import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Image,
  FlatList,
  Animated,
  Dimensions,
  TextInput,
  StyleSheet,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { useLiveStream } from "../../context/LiveStreamContext";
import { PanGestureHandler } from "react-native-gesture-handler";
import { toast } from "react-toastify";
import ModalLikeAnimation from "./ModalLikeAnimation";
import StreamActionIcon from "../StreamActionIcons";
import Avatar from "../choose-avatar-components/Avatar";

const { height: screenHeight } = Dimensions.get("window");

interface MobileChatBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  sendLike?: () => void;
  alreadyLiked?: boolean;
  setOrganizerData?: (fn: (prev: any) => any) => void;
  setAudienceData?: (fn: (prev: any) => any) => void;
}

const MobileChatBottomSheet: React.FC<MobileChatBottomSheetProps> = ({
  visible,
  onClose,
  sendLike,
  alreadyLiked = false,
  setOrganizerData,
  setAudienceData,
}) => {
  const { audienceData, sendComment, userData, currentEventId } =
    useLiveStream();

  const flatListRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const [newMessage, setNewMessage] = useState("");
  const [pendingMessages, setPendingMessages] = useState<any[]>([]);
  const [likeAnimations, setLikeAnimations] = useState<number[]>([]);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const slideAnim = useRef(new Animated.Value(screenHeight * 0.5)).current;

  // Filter and sort chat data properly (same as StreamChat)
  const audienceChatData = useMemo(() => {
    const chats = audienceData?.chats || [];
    return chats
      .filter((chat: any) => chat.commentType === "Audience")
      .sort((a: any, b: any) => a.timestamp - b.timestamp);
  }, [audienceData?.chats]);

  const audienceCommentIds = useMemo(() => {
    return new Set((audienceChatData || []).map((c: any) => c.commentId));
  }, [audienceChatData]);

  // Drop pending messages once they are echoed back by server
  useEffect(() => {
    if (pendingMessages.length === 0) return;
    setPendingMessages((prev) =>
      prev.filter((m) => !audienceCommentIds.has(m.commentId))
    );
  }, [audienceCommentIds]);

  // Combine real and pending messages
  const streamChatData = useMemo(() => {
    const combined = [...audienceChatData, ...pendingMessages];
    const byId = new Map<string, any>();
    combined.forEach((m) => {
      if (!byId.has(m.commentId)) byId.set(m.commentId, m);
    });
    return Array.from(byId.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [audienceChatData, pendingMessages]);

  useEffect(() => {
    if (visible) {
      // Slide up when visible
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 10,
      }).start();
    } else {
      // Slide down when not visible
      Animated.timing(slideAnim, {
        toValue: screenHeight * 0.5,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible]);

  // Handle scroll position detection
  const handleScroll = useCallback((event: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const paddingToBottom = 20;
    const isBottom =
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom;

    setIsAtBottom(isBottom);
    setShowScrollToBottom(!isBottom && streamChatData.length > 0);
  }, [streamChatData.length]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (!flatListRef.current || streamChatData.length === 0) return;
    try {
      flatListRef.current.scrollToEnd({ animated: true });
      setShowScrollToBottom(false);
    } catch (e) {
      console.error("Error scrolling to bottom:", e);
    }
  }, [streamChatData.length]);

  // Auto-scroll only when user is at bottom
  useEffect(() => {
    if (streamChatData && flatListRef.current && isAtBottom) {
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [streamChatData, isAtBottom]);

  const handleGestureEnd = (event: any) => {
    // Disabled swipe-to-close - only allow closing by clicking outside
    // This prevents accidental closure when scrolling
  };

  const handleSendComment = useCallback(() => {
    if (newMessage.trim() === "") {
      toast.error("Please enter a message");
      return;
    }

    const userId = userData?.userID;
    const eventId = currentEventId;
    if (!userId || !eventId) {
      toast.error("Unable to send message. Try reloading the stream.");
      return;
    }

    const newComment = {
      userId,
      eventId,
      commentData: newMessage,
      commentType: "Audience",
      timestamp: Date.now(),
      profilePicture: userData?.profilePicture || "",
      commentId: `${userId}-${Date.now()}`,
      senderName: userData?.username || "Anonymous",
      message: newMessage,
    };
    setPendingMessages((prev) => [...prev, newComment]);

    sendComment(userId, eventId, newMessage, "Audience");

    setNewMessage("");

    // Scroll to latest message after sending
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true });
    }, 100);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
  }, [
    currentEventId,
    newMessage,
    sendComment,
    userData?.profilePicture,
    userData?.userID,
    userData?.username,
  ]);

  const handleAnimationComplete = useCallback((animationId: number) => {
    setLikeAnimations((prev) => prev.filter((id) => id !== animationId));
  }, []);

  const renderChatItem = ({ item }: { item: any }) => (
    <View
      style={{
        flexDirection: "row",
        marginVertical: 8,
        paddingHorizontal: 16,
      }}
    >
      {item.profilePicture ? (
        <View
          style={{
            width: 32,
            height: 32,
            backgroundColor: AppColors.white,
            borderRadius: 32,
            overflow: "hidden",
            marginRight: 12,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Image
            source={{ uri: item.profilePicture }}
            style={{
              width: 32,
              height: 32,
            }}
            resizeMode="cover"
          />
        </View>
      ) : (
        <Image
          source={AppImages.Ellipse1}
          style={{
            width: 32,
            height: 32,
            borderRadius: 100,
            marginRight: 12,
          }}
          resizeMode="contain"
        />
      )}
      <View style={{ flex: 1 }}>
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_14,
            {
              color: AppColors.koBrandRed,
              fontSize: 13,
              fontWeight: "600",
              marginBottom: 2,
            },
          ]}
        >
          {item.userId || item.senderName || item.username || "Anonymous"}
        </Text>
        <Text
          style={[
            AppFontStyle.REGULAR_ANYBODY_14,
            {
              color: "#E0E0E0",
              fontSize: 14,
              lineHeight: 18,
            },
          ]}
        >
          {item.message || item.commentData}
        </Text>
      </View>
    </View>
  );

  return (
    <Modal transparent visible={visible} animationType="none">
      <View style={{ flex: 1 }}>
        <TouchableOpacity
          style={{ flex: 1 }}
          onPress={onClose}
          activeOpacity={1}
        />

        <PanGestureHandler onHandlerStateChange={handleGestureEnd}>
          <Animated.View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: screenHeight * 0.5,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              transform: [{ translateY: slideAnim }],
              overflow: "hidden",
            }}
          >
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                flex: 1,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                padding: 15,
              }}
            >
              {/* Messages List */}
              <FlatList
                showsHorizontalScrollIndicator={false}
                showsVerticalScrollIndicator={false}
                ref={flatListRef}
                onScroll={handleScroll}
                scrollEventThrottle={16}
                style={{
                  flex: 1,
                  marginBottom: 15,
                }}
                contentContainerStyle={{
                  flexGrow: 1,
                  paddingVertical: 8,
                }}
                data={streamChatData}
                keyExtractor={(item: any) => item.commentId}
                renderItem={renderChatItem}
              />

              {/* Scroll to Bottom Button */}
              {showScrollToBottom && (
                <TouchableOpacity
                  onPress={scrollToBottom}
                  style={{
                    position: "absolute",
                    bottom: 70,
                    right: 10,
                    backgroundColor: "rgba(220, 38, 38, 0.9)",
                    borderRadius: 20,
                    width: 40,
                    height: 40,
                    justifyContent: "center",
                    alignItems: "center",
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.3,
                    shadowRadius: 4,
                    elevation: 5,
                    zIndex: 1000,
                  }}
                >
                  <Text style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>
                    ↓
                  </Text>
                </TouchableOpacity>
              )}

              {/* Input Section - Chat and Like */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {/* Text Input with Send Button */}
                <View
                  style={{
                    flex: 1,
                    height: 44,
                    position: "relative",
                  }}
                >
                  <TextInput
                    ref={inputRef}
                    style={{
                      flex: 1,
                      height: 44,
                      paddingVertical: 0,
                      paddingHorizontal: 16,
                      borderRadius: 8,
                      paddingRight: 50,
                      backgroundColor: "#1F2937",
                      fontSize: 14,
                      borderWidth: 0.8,
                      borderColor: "#374151",
                      color: "#FFFFFF",
                    }}
                    placeholderTextColor="#999999"
                    placeholder="Write Here"
                    value={newMessage}
                    onChangeText={setNewMessage}
                    onSubmitEditing={handleSendComment}
                    multiline={false}
                  />

                  {/* Send Button - Absolute Positioned Inside Input */}
                  <View
                    style={{
                      position: "absolute",
                      right: 2,
                      top: 2,
                      bottom: 2,
                    }}
                  >
                    <StreamActionIcon type="send" onPress={handleSendComment} />
                  </View>
                </View>

                {sendLike && (
                  <StreamActionIcon
                    type="like"
                    onPress={() => {
                      const animationId = Date.now();
                      setLikeAnimations((prev) => [...prev, animationId]);

                      sendLike();
                    }}
                  />
                )}
              </View>

              {likeAnimations.map((animationId) => (
                <ModalLikeAnimation
                  key={animationId}
                  onComplete={() => handleAnimationComplete(animationId)}
                />
              ))}
            </LinearGradient>
          </Animated.View>
        </PanGestureHandler>
      </View>
    </Modal>
  );
};

export default MobileChatBottomSheet;
